/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { IModelConnection } from "@itwin/core-frontend";
import { HiliteSet, HiliteSetProvider, Presentation } from "@itwin/presentation-frontend";
import { KeySet } from "@itwin/presentation-common";

/**
 * This class contains functions for hiliting elements
 * changes in hilitelmentapi
 */
export class HiliteElementsApi {
  // The existance of a HiliteSetProvider is required for calculating which elements are hilited in the iModel
  private _hiliteSetProvider: HiliteSetProvider;

  constructor(iModel: IModelConnection) {
    this._hiliteSetProvider = HiliteSetProvider.create({imodel: iModel});
  }

  public async getHiliteSetElementIds(iModel: IModelConnection, keySet: KeySet ): Promise<string[]> {
    const hiliteSet: HiliteSet = await this._hiliteSetProvider.getHiliteSet(keySet);
    if (hiliteSet.elements !== undefined) {
      return hiliteSet.elements;
    }
    return [];
  };

  /**
   * Calculate the element ids that should be hilited based on the provided elementId and scope.
   * @param iModel The iModel in the Viewer.
   * @param elementId The element id that would have been selected in Element Scope.
   * @param targetScopeId The selection scope we want to use instead of Element Scope (Element Scope may be passed here)
   *                      for deciding what should be hilited.
   * @returns An array of element ids.
   */
  public async getHiliteSetElementIdsForGivenScope(iModel: IModelConnection, elementId: string, targetScopeId: string): Promise<string[]> {
    const keySet = await Presentation.selection.scopes.computeSelection(iModel, elementId, targetScopeId);
    const hiliteSet: HiliteSet = await this._hiliteSetProvider.getHiliteSet(keySet);
    if (hiliteSet.elements !== undefined) {
      return hiliteSet.elements;
    }
    return [];
  }
}
